import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  assetsInCone,
  formatUscgFocAssetLabel,
} from './uscgFocSoutheastTropicalData';
import {
  isUscgFocSeResourceSet,
  uscgFocSeResourceNotesMarker,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const FOC_RESOURCE_KIND = 'Facilities';
export const USCG_FOC_SE_SYMBOLOGY = 'facility';

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchMissingSymbology({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const missing = resources.filter(
    (resource) => isUscgFocSeResourceSet(resource) && !resource.symbology
  );
  if (missing.length === 0) return false;

  await Promise.all(
    missing.map((resource) => {
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: USCG_FOC_SE_SYMBOLOGY,
      };
      const { members: _members, ...dataForBackend } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureUscgFocSeResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isUscgFocSeResourceSet)
      .map((resource) => resource.notes as string)
  );

  const toCreate = assetsInCone().filter(
    (asset) => !existingMarkers.has(uscgFocSeResourceNotesMarker(asset.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    await Promise.all(
      toCreate.map((asset) => {
        const notes = uscgFocSeResourceNotesMarker(asset.id);
        return axios.post(config.apiGateway.ics_resource_sets, {
          name: formatUscgFocAssetLabel(asset),
          description: `${asset.name} (${asset.city}, ${asset.state}) — USCG ${asset.unitType} inside the Helene forecast cone (Southeast District).`,
          kind: FOC_RESOURCE_KIND,
          type: asset.unitType,
          quantity: 1,
          status: 'In Organization',
          asset_status: 'PMC',
          asset_status_timestamp: new Date().toISOString(),
          address: `${asset.city}, ${asset.state}`,
          symbology: USCG_FOC_SE_SYMBOLOGY,
          notes,
          geometry: {
            type: 'Point',
            coordinates: [asset.lon, asset.lat],
          },
          incidents: [],
          archived: false,
          group_guid: groupGuid,
          user_guid: userGuid,
          incident_id: null,
        });
      })
    );
    didMutate = true;
  }

  const patched = await patchMissingSymbology({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

/** When FOC is on, ensure each Southeast cone asset exists as a Hub resource set. */
export function useEnsureUscgFocSeResourceSets() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureUscgFocSeResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[USCG FOC] Failed to ensure Southeast cone resource sets',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [focEnabled, groupGuid, userGuid, queryClient]);
}
